import { useCallback, type ReactNode } from 'react';

import { Badge, type BadgeTone } from '@/shared/ui/badge';
import { StatCard } from '@/shared/ui/stat-card';
import { dashboardApi } from '@/modules/dashboard/services/api';
import type { OnboardingCategory, OnboardingReadiness } from '@/api/schemas/dashboard';
import { DashboardWidgetFrame, type DashboardWidgetProps } from './widget-frame';

/**
 * The tenant's SETUP COMPLETENESS.
 *
 * ---------------------------------------------------------------------------
 * THE PLATFORM'S OWN PROJECTION, NOT A CHECKLIST OF THE CONSOLE'S OWN
 * ---------------------------------------------------------------------------
 * The API already states how complete a tenant's setup is, per category, and — for anything the
 * platform does not implement — WHY. The dashboard prints that projection rather than counting
 * its own idea of what a tenant should have set up, because the two would answer different
 * questions and the operator would be left wondering which one to believe.
 *
 * `not_supported` is NOT `missing`. A category the platform does not implement cannot be
 * completed by anyone, so counting it as something the tenant still owes would be a lie about
 * the tenant's work. Its reason is printed instead.
 */
const STATUS_TONE: Record<OnboardingCategory['status'], BadgeTone> = {
  complete: 'positive',
  missing: 'caution',
  not_supported: 'neutral',
};

const STATUS_LABEL: Record<OnboardingCategory['status'], string> = {
  complete: 'Complete',
  missing: 'Missing',
  not_supported: 'Not supported',
};

function CategoryRow({ category }: { readonly category: OnboardingCategory }): ReactNode {
  return (
    <li className="flex flex-col gap-1 border-b border-border py-2 last:border-b-0">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-content">{category.category.replace(/_/g, ' ')}</span>
        <Badge tone={STATUS_TONE[category.status]}>{STATUS_LABEL[category.status]}</Badge>
      </div>
      {category.reason === undefined ? null : (
        <p className="text-xs text-content-muted">{category.reason}</p>
      )}
    </li>
  );
}

function SetupCompletenessBody({ data }: { readonly data: OnboardingReadiness }): ReactNode {
  const unsupported = data.categories.filter((category) => category.status === 'not_supported');

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <StatCard
          label="Setup"
          value={data.overall === 'complete' ? 'Complete' : 'Incomplete'}
          tone={data.overall === 'complete' ? 'positive' : 'warning'}
          caption={data.overall === 'complete' ? 'Nothing outstanding' : 'The platform says so'}
        />
        <StatCard
          label="Still missing"
          value={String(data.missingCount)}
          tone={data.missingCount === 0 ? 'positive' : 'warning'}
          caption={
            data.missingCount === 0
              ? 'Every supported category is done'
              : 'Categories the tenant can still complete'
          }
        />
      </div>

      {unsupported.length === 0 ? null : (
        <p className="text-xs text-content-muted">
          {unsupported.length} categor{unsupported.length === 1 ? 'y is' : 'ies are'} not
          supported by the platform and are not counted as outstanding.
        </p>
      )}

      <ul className="flex flex-col">
        {data.categories.map((category) => (
          <CategoryRow key={category.category} category={category} />
        ))}
      </ul>
    </div>
  );
}

export function SetupCompleteness({ className }: DashboardWidgetProps): ReactNode {
  const load = useCallback(() => dashboardApi.onboarding(), []);

  return (
    <DashboardWidgetFrame
      title="Setup completeness"
      load={load}
      className={className}
      isEmpty={(data) => data.categories.length === 0}
      emptyTitle="No setup categories yet"
      emptyDescription="The platform has not reported this tenant's setup categories."
    >
      {(data) => <SetupCompletenessBody data={data} />}
    </DashboardWidgetFrame>
  );
}
